import { apiRequest } from "./api.client";
import type { PaymentMode } from "./expense.service";

export interface IncomeSource {
  source_id: string;
  clinic_id: string | null;
  name: string;
  code: string;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
}

export interface IncomeEntry {
  income_id: string;
  clinic_id: string;
  source_id: string;
  amount_paise: number;
  income_date: string;
  counterparty: string | null;
  payment_mode: PaymentMode | null;
  payment_reference: string | null;
  receipt_s3_key: string | null;
  detail_json: Record<string, unknown> | null;
  notes: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface IncomeEntryCreatePayload {
  source_id: string;
  amount_paise: number;
  income_date: string;
  counterparty?: string;
  payment_mode?: PaymentMode;
  payment_reference?: string;
  detail_json?: Record<string, unknown>;
  notes?: string;
}

export interface IncomeListFilters {
  date_from?: string;
  date_to?: string;
  source_id?: string;
  counterparty?: string;
  skip?: number;
  limit?: number;
}

function qs(params: object): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(
    ([k, v]) => v !== undefined && v !== null && search.set(k, String(v))
  );
  const s = search.toString();
  return s ? `?${s}` : "";
}

export const IncomeAPI = {
  listSources: () => apiRequest<IncomeSource[]>("/income-sources"),

  createSource: (name: string, code: string) =>
    apiRequest<IncomeSource>("/income-sources", {
      method: "POST",
      body: JSON.stringify({ name, code }),
    }),

  deactivateSource: (sourceId: string) =>
    apiRequest<null>(`/income-sources/${sourceId}`, { method: "DELETE" }),

  list: (filters: IncomeListFilters = {}) =>
    apiRequest<IncomeEntry[]>(`/income-entries${qs(filters)}`),

  get: (incomeId: string) => apiRequest<IncomeEntry>(`/income-entries/${incomeId}`),

  create: (payload: IncomeEntryCreatePayload) =>
    apiRequest<IncomeEntry>("/income-entries", { method: "POST", body: JSON.stringify(payload) }),

  update: (incomeId: string, payload: Partial<IncomeEntryCreatePayload>) =>
    apiRequest<IncomeEntry>(`/income-entries/${incomeId}`, { method: "PUT", body: JSON.stringify(payload) }),

  delete: (incomeId: string) => apiRequest<null>(`/income-entries/${incomeId}`, { method: "DELETE" }),

  getReceiptUploadUrl: (incomeId: string) =>
    apiRequest<{ upload_url: string | null; s3_key: string }>(`/income-entries/${incomeId}/receipt-upload-url`),

  confirmReceipt: (incomeId: string, s3Key: string) =>
    apiRequest<IncomeEntry>(`/income-entries/${incomeId}/receipt/confirm`, {
      method: "POST",
      body: JSON.stringify({ s3_key: s3Key }),
    }),

  getReceiptViewUrl: (incomeId: string) =>
    apiRequest<{ view_url: string }>(`/income-entries/${incomeId}/receipt-url`),
};

export default IncomeAPI;
